import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, ArrowRight, ArrowLeft, KeyRound, ShieldCheck, ShieldOff, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Persisted gate-verification expiry. Stores the ISO timestamp at which the
// visitor must re-verify; absent / past → gate shows. We use localStorage
// (not sessionStorage) so closing the tab — or even closing the whole browser
// — does NOT force a re-verify within the validity window.
const STORAGE_KEY = "sv_gate_verified_until";
const VERIFY_VALIDITY_MS = 7 * 24 * 60 * 60 * 1000; // 7 days per user request (was 48h)
// Persisted (localStorage) ID of the visitor as recorded by /api/gate/verify.
// We persist across tab sessions so a repeat visit links back to the same row
// in admin (cumulative views, single lead per phone).
const VISITOR_ID_KEY = "sv_gate_visitor_id";

// Returns true if the current device has a non-expired gate verification.
// Survives across tabs, refreshes, and full browser restarts.
function isStillVerified() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const ts = Date.parse(raw);
    if (Number.isNaN(ts)) return false;
    return ts > Date.now();
  } catch {
    return false;
  }
}

// =====================================================================
// PASTE YOUR ZAPIER / MAKE / N8N / CUSTOM WEBHOOK URL BELOW
// =====================================================================
// The gate POSTs a JSON body like
//   { phone, verified_at, user_agent, referrer }
// to this URL the instant a public visitor completes the self-WhatsApp
// OTP. Use it to fire a Zap that drops the lead into Google Sheets, sends
// you a WhatsApp/email notification, or whatever. Leave as "" to silently
// disable the ping.
const NOTIFY_WEBHOOK_URL = "";
// =====================================================================

// India-centric phone normaliser: if the user typed exactly 10 digits we
// assume Indian mobile and prefix `91`; otherwise we use whatever they typed
// verbatim (covers NRIs, traders abroad).
const sanitisePhone = (raw) => {
  const digits = (raw || "").replace(/\D/g, "");
  if (digits.length === 10) return "91" + digits;
  return digits;
};
const isValidPhone = (digits) => digits.length >= 10 && digits.length <= 15;

/**
 * Drop-in non-dismissible gate overlay for the public homepage / catalog.
 *
 * Renders nothing if (a) the user is logged in (any role), or (b) they've
 * already verified in this tab session. Otherwise it occludes the whole
 * viewport with a centred card offering two flows:
 *
 *   1. Public verification via "Self-WhatsApp OTP" — visitor enters their
 *      WhatsApp number, we generate a random 4-digit code, open wa.me/<n>
 *      with the code prefilled in a new tab so they message themselves,
 *      they read the code from their WhatsApp and type it back. On match
 *      we unlock + ping the admin webhook.
 *
 *   2. Registered buyer login — same component swaps to a phone+password
 *      form that hits the real /api/auth/login endpoint via AuthContext.
 *      On success we redirect to /catalog (which serves wholesale pricing
 *      to approved traders automatically).
 *
 * Security note (acknowledged in spec): the OTP is generated client-side
 * so a sufficiently motivated visitor can bypass it via DevTools. The
 * purpose is lead capture + light friction, not real security.
 */
export default function HomepageGate() {
  const { user, login } = useAuth();
  const navigate = useNavigate();

  const [unlocked, setUnlocked] = useState(() => isStillVerified());
  const [mode, setMode] = useState("verify"); // "verify" | "login"
  // Set to true when the backend reports our cached visitor_id has been
  // blocked by the admin. We show a dedicated "access removed" screen
  // instead of the verify form so the visitor can't just re-enter their
  // number and waltz back in.
  const [blocked, setBlocked] = useState(false);

  // Verify-flow state
  const [phase, setPhase] = useState("enter-phone"); // "enter-phone" | "enter-code"
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState(null); // generated 4-digit code, kept in memory only
  const [entered, setEntered] = useState("");
  const [busy, setBusy] = useState(false);

  // Login-flow state
  const [lPhone, setLPhone] = useState("");
  const [lPwd, setLPwd] = useState("");
  const [lBusy, setLBusy] = useState(false);
  const [lError, setLError] = useState("");

  // If the user logs in (this tab or another), drop the gate silently.
  useEffect(() => { if (user) setUnlocked(true); }, [user]);

  // Bootstrap: if we have a cached visitor_id, ask the backend whether the
  // admin has blocked them since the last visit. If yes, wipe both
  // localStorage keys and force the gate to show with the blocked screen.
  // Logged-in users are skipped — the block applies to public visitors only.
  useEffect(() => {
    if (user) return;
    let visitorId = null;
    try { visitorId = localStorage.getItem(VISITOR_ID_KEY); } catch { /* private mode */ }
    if (!visitorId) return;
    api.post("/gate/check", { visitor_id: visitorId })
      .then(({ data }) => {
        if (data?.blocked) {
          try {
            localStorage.removeItem(STORAGE_KEY);
            localStorage.removeItem(VISITOR_ID_KEY);
          } catch { /* ignore */ }
          setBlocked(true);
          setUnlocked(false);
        }
      })
      .catch(() => { /* network failure must not lock out a legitimate user */ });
  }, [user]);

  // Block background scrolling while the gate is up — the underlying catalog
  // would otherwise scroll behind the modal on iOS.
  useEffect(() => {
    if (unlocked) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [unlocked]);

  if (unlocked) return null;

  const handleGetCode = () => {
    const digits = sanitisePhone(phone);
    if (!isValidPhone(digits)) {
      toast.error("Enter a valid 10-digit WhatsApp number");
      return;
    }
    // 4-digit numeric, padded so we never get "0042" → "42" UX weirdness.
    const generated = String(Math.floor(1000 + Math.random() * 9000));
    setCode(generated);
    setPhase("enter-code");
    const msg = `Your verification code is: ${generated}`;
    const url = `https://wa.me/${digits}?text=${encodeURIComponent(msg)}`;
    // `_blank` keeps our gate visible in the original tab so the user can
    // alt-tab back and enter the code.
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const reopenWhatsapp = () => {
    if (!code) return;
    const digits = sanitisePhone(phone);
    const url = `https://wa.me/${digits}?text=${encodeURIComponent(`Your verification code is: ${code}`)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleVerify = async () => {
    if (entered.trim() !== code) {
      toast.error("Code doesn't match — check your WhatsApp and try again");
      return;
    }
    setBusy(true);
    // 1. Persist the lead on OUR backend so the admin gets a structured
    //    "Verified Visitors" list with cumulative series-view tracking.
    //    A 403 here means the admin has explicitly blocked this number —
    //    we MUST NOT unlock the gate in that case (different from a network
    //    error, which we ignore so a transient backend blip never locks a
    //    legitimate visitor out).
    try {
      const { data } = await api.post("/gate/verify", {
        phone: sanitisePhone(phone),
        user_agent: navigator.userAgent,
        referrer: document.referrer || null,
      });
      if (data?.visitor_id) {
        try { localStorage.setItem(VISITOR_ID_KEY, data.visitor_id); } catch { /* quota */ }
      }
    } catch (err) {
      if (err?.response?.status === 403) {
        setBusy(false);
        setBlocked(true);
        return; // hard stop — show "access removed" screen
      }
      /* other failures: keep going — gate unlock is paramount */
    }

    // 2. Fire the legacy webhook (Zapier / Make / n8n) if the user configured one.
    // Fire-and-forget admin notification. `no-cors` lets us POST to Zapier /
    // Make / n8n hooks that don't return CORS headers. We don't need to read
    // the response — the side effect (a row in your Zap) is the point.
    if (NOTIFY_WEBHOOK_URL) {
      try {
        await fetch(NOTIFY_WEBHOOK_URL, {
          method: "POST",
          mode: "no-cors",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            phone: sanitisePhone(phone),
            verified_at: new Date().toISOString(),
            user_agent: navigator.userAgent,
            referrer: document.referrer || null,
          }),
        });
      } catch { /* webhook failure must never block the user */ }
    }
    try {
      const expiresAt = new Date(Date.now() + VERIFY_VALIDITY_MS).toISOString();
      localStorage.setItem(STORAGE_KEY, expiresAt);
    } catch { /* quota / private mode — gate will just re-prompt next visit */ }
    setBusy(false);
    setUnlocked(true);
    toast.success("Welcome — browse our catalog");
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLError("");
    setLBusy(true);
    try {
      const u = await login(lPhone.trim(), lPwd);
      // Registered buyer logged in — redirect to the wholesale catalog. Admins
      // get sent to their dashboard instead so they don't land in the customer
      // browsing view.
      navigate(u?.role === "admin" ? "/admin" : "/catalog", { replace: true });
    } catch (err) {
      setLError(err?.response?.data?.detail || "Invalid phone or password");
    } finally {
      setLBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-[#1A1A1A]/85 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="homepage-gate-title"
      data-testid="homepage-gate-overlay"
    >
      <div
        className="w-full max-w-md bg-white border border-[#F4A100] shadow-2xl my-auto"
        data-testid="homepage-gate-card"
      >
        <div className="bg-[#D62828] text-white text-center py-5 px-6">
          <div className="font-serif text-2xl" id="homepage-gate-title">Shubh Vivah</div>
          <div className="text-[10px] md:text-[11px] font-semibold tracking-[0.18em] text-[#F4A100] mt-1">by PAPER KRAFT</div>
          <div className="tracking-luxe text-[9px] text-white/70 mt-0.5">WEDDING CARD ATELIER</div>
        </div>

        {blocked && (
          <div className="p-6 space-y-4 text-center" data-testid="gate-blocked-state">
            <div className="w-12 h-12 bg-[#FEF2F2] border border-[#FCA5A5] rounded-full mx-auto flex items-center justify-center">
              <ShieldOff className="w-6 h-6 text-[#991B1B]" strokeWidth={1.3} />
            </div>
            <h2 className="font-serif text-lg text-[#1A1A1A]">Access removed</h2>
            <p className="text-xs text-[#4A4A4A] leading-relaxed">
              This number is no longer permitted to view our public catalog.
              If you believe this is a mistake, please get in touch with us directly.
            </p>
            <a
              href="/contact"
              className="inline-block bg-[#D62828] hover:bg-[#A41C1C] text-white tracking-luxe text-xs px-6 py-2.5 transition-colors"
              data-testid="gate-blocked-contact"
            >
              CONTACT THE ATELIER
            </a>
          </div>
        )}

        {!blocked && mode === "verify" && phase === "enter-phone" && (
          <div className="p-6 space-y-5" data-testid="gate-verify-phone-step">
            <div className="text-center">
              <ShieldCheck className="w-10 h-10 text-[#D62828] mx-auto mb-2" strokeWidth={1.3} />
              <h2 className="font-serif text-lg text-[#1A1A1A]">Verify to view catalog</h2>
              <p className="text-xs text-[#4A4A4A] mt-1 leading-relaxed">
                Enter your WhatsApp number — we&apos;ll send a code to your own WhatsApp to confirm it&apos;s really you.
              </p>
            </div>
            <div>
              <label className="block tracking-luxe text-[10px] text-[#4A4A4A] mb-1.5">WHATSAPP NUMBER</label>
              <div className="flex items-stretch border border-[#E5E0D8] focus-within:border-[#D62828]">
                <span className="px-3 flex items-center text-sm text-[#4A4A4A] border-r border-[#E5E0D8] bg-[#FAF9F6]">+91</span>
                <input
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  placeholder="9876543210"
                  className="flex-1 px-3 py-2.5 text-sm focus:outline-none"
                  data-testid="gate-phone-input"
                />
              </div>
            </div>
            <button
              type="button"
              onClick={handleGetCode}
              disabled={phone.length < 10}
              className="w-full bg-[#D62828] hover:bg-[#A41C1C] disabled:opacity-50 disabled:cursor-not-allowed text-white tracking-luxe text-[11px] py-3 flex items-center justify-center gap-2"
              data-testid="gate-get-code-btn"
            >
              <MessageCircle size={14} /> GET CODE ON WHATSAPP
            </button>
            <div className="text-center pt-3 border-t border-[#F0EBE0] space-y-2">
              <button
                type="button"
                onClick={() => setMode("login")}
                className="block w-full text-xs text-[#D62828] hover:text-[#A41C1C] underline underline-offset-2"
                data-testid="gate-show-login"
              >
                Registered Buyer? Login Here →
              </button>
              <button
                type="button"
                onClick={() => navigate("/register")}
                className="block w-full text-xs text-[#4A4A4A] hover:text-[#D62828]"
                data-testid="gate-show-register"
              >
                New trader? <span className="underline underline-offset-2 text-[#D62828]">Create a wholesale account →</span>
              </button>
            </div>
          </div>
        )}

        {!blocked && mode === "verify" && phase === "enter-code" && (
          <div className="p-6 space-y-5" data-testid="gate-verify-code-step">
            <div className="text-center">
              <KeyRound className="w-10 h-10 text-[#D62828] mx-auto mb-2" strokeWidth={1.3} />
              <h2 className="font-serif text-lg text-[#1A1A1A]">Check your WhatsApp</h2>
              <p className="text-xs text-[#4A4A4A] mt-1 leading-relaxed">
                We opened WhatsApp with a 4-digit code addressed to <span className="text-[#D62828] font-medium">+91 {phone}</span>. Tap <span className="font-medium">Send</span> in WhatsApp, then come back here and type the code.
              </p>
            </div>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={entered}
              onChange={(e) => setEntered(e.target.value.replace(/\D/g, "").slice(0, 4))}
              placeholder="• • • •"
              maxLength={4}
              className="w-full text-center tracking-[0.4em] text-2xl py-3 border border-[#E5E0D8] focus:border-[#D62828] focus:outline-none"
              data-testid="gate-otp-input"
              autoFocus
            />
            <button
              type="button"
              onClick={handleVerify}
              disabled={entered.length !== 4 || busy}
              className="w-full bg-[#D62828] hover:bg-[#A41C1C] disabled:opacity-50 disabled:cursor-not-allowed text-white tracking-luxe text-[11px] py-3 flex items-center justify-center gap-2"
              data-testid="gate-verify-btn"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
              VERIFY &amp; ENTER
            </button>
            <div className="flex items-center justify-between text-[11px] gap-3">
              <button
                type="button"
                onClick={() => { setPhase("enter-phone"); setEntered(""); setCode(null); }}
                className="text-[#4A4A4A] hover:text-[#D62828] flex items-center gap-1"
                data-testid="gate-change-number"
              >
                <ArrowLeft size={12} /> Change number
              </button>
              <button
                type="button"
                onClick={reopenWhatsapp}
                className="text-[#D62828] hover:text-[#A41C1C] underline text-right"
                data-testid="gate-resend"
              >
                Didn&apos;t receive? Open WhatsApp again
              </button>
            </div>
          </div>
        )}

        {!blocked && mode === "login" && (
          <form onSubmit={handleLogin} className="p-6 space-y-5" data-testid="gate-login-form">
            <div className="text-center">
              <h2 className="font-serif text-lg text-[#1A1A1A]">Registered Buyer Login</h2>
              <p className="text-xs text-[#4A4A4A] mt-1">For approved wholesale customers</p>
            </div>
            <div>
              <label className="block tracking-luxe text-[10px] text-[#4A4A4A] mb-1.5">PHONE NUMBER</label>
              <input
                type="tel"
                inputMode="numeric"
                autoComplete="tel"
                value={lPhone}
                onChange={(e) => setLPhone(e.target.value)}
                placeholder="9876543210"
                required
                className="w-full px-3 py-2.5 text-sm border border-[#E5E0D8] focus:border-[#D62828] focus:outline-none"
                data-testid="gate-login-phone"
              />
            </div>
            <div>
              <label className="block tracking-luxe text-[10px] text-[#4A4A4A] mb-1.5">PASSWORD</label>
              <input
                type="password"
                autoComplete="current-password"
                value={lPwd}
                onChange={(e) => setLPwd(e.target.value)}
                required
                className="w-full px-3 py-2.5 text-sm border border-[#E5E0D8] focus:border-[#D62828] focus:outline-none"
                data-testid="gate-login-password"
              />
            </div>
            {lError && (
              <div className="text-xs text-[#9B1C1C] bg-[#FDF3F3] border border-[#F1D5D5] px-3 py-2" data-testid="gate-login-error">
                {lError}
              </div>
            )}
            <button
              type="submit"
              disabled={lBusy || !lPhone || !lPwd}
              className="w-full bg-[#D62828] hover:bg-[#A41C1C] disabled:opacity-50 disabled:cursor-not-allowed text-white tracking-luxe text-[11px] py-3 flex items-center justify-center gap-2"
              data-testid="gate-login-submit"
            >
              {lBusy ? <Loader2 size={14} className="animate-spin" /> : null}
              SIGN IN
            </button>
            <div className="text-center pt-3 border-t border-[#F0EBE0] space-y-2">
              <button
                type="button"
                onClick={() => navigate("/register")}
                className="block w-full text-xs text-[#4A4A4A] hover:text-[#D62828]"
                data-testid="gate-login-show-register"
              >
                New trader? <span className="underline underline-offset-2 text-[#D62828]">Create a wholesale account →</span>
              </button>
              <button
                type="button"
                onClick={() => setMode("verify")}
                className="text-xs text-[#D62828] hover:text-[#A41C1C] inline-flex items-center gap-1"
                data-testid="gate-back-to-verify"
              >
                <ArrowLeft size={12} /> Back to public verification
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
